import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShieldCheck, XCircle, Loader2 } from "lucide-react";
import { http } from "@/lib/api";

/**
 * Public landing for the one-time nominee verification link sent by
 * ``POST /me/nominee/verify-send``. Consumes the token, marks the
 * nominee as verified, and shows a friendly confirmation.
 */
export default function NomineeAccept() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // "loading" | "ok" | "already" | "error"
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.post(`/nominee/claim/${token}`);
        setStatus(data.already_verified ? "already" : "ok");
      } catch (err) {
        setErrMsg(err?.response?.data?.detail || "This link is invalid or has expired.");
        setStatus("error");
      }
    })();
  }, [token]);

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="nominee-accept-page">
      <Helmet><title>{`Nominee Acknowledgement`}</title></Helmet>
      {status === "loading" && (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: "#B58936" }} />
          <p className="text-sm opacity-70">Verifying your appointment…</p>
        </div>
      )}
      {(status === "ok" || status === "already") && (
        <div>
          <ShieldCheck className="w-14 h-14 mx-auto" style={{ color: "#5C6B52" }} />
          <h1 className="mt-4 font-display text-3xl" style={{ color: "#3B2E1F" }}>
            {status === "already" ? "You're already verified." : "Thank you — appointment acknowledged."}
          </h1>
          <p className="mt-3 text-sm" style={{ color: "#6B5A3F" }}>
            You've been designated as a nominee under India's DPDP Act, 2023.
            If the account holder ever passes away, our team will reach out to help you inherit their family archive.
            You don't need to do anything else right now.
          </p>
          <Link to="/" className="wofy-btn-primary mt-6 text-sm inline-flex" data-testid="nominee-accept-home">Return home</Link>
        </div>
      )}
      {status === "error" && (
        <div>
          <XCircle className="w-14 h-14 mx-auto" style={{ color: "#B23A3A" }} />
          <h1 className="mt-4 font-display text-3xl" style={{ color: "#3B2E1F" }}>Link no longer works.</h1>
          <p className="mt-3 text-sm" style={{ color: "#6B5A3F" }}>{errMsg}</p>
          <p className="mt-3 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            Ask the account holder to resend the verification email from their account settings.
          </p>
        </div>
      )}
    </div>
  );
}
